import classNames from "classnames";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
} from "@/components";
import type { ApiDevice } from "../../_types/devices.types";
import { formatDeviceTimestamp, formatDeviceTypeLabel } from "../../_utils/formatDevice";
import { DotsVerticalIcon } from "@vetty/icons";
import styles from "./DeviceListItem.module.scss";

type DeviceListItemProps = {
  device: ApiDevice;
  onRowClick: () => void;
  onEdit: () => void;
  onRequestDelete: () => void;
  actionsDisabled: boolean;
};

export const DeviceListItem = ({
  device,
  onRowClick,
  onEdit,
  onRequestDelete,
  actionsDisabled,
}: DeviceListItemProps) => {
  const lastMs = device.lastSeenAt ?? device.lastHeartbeat ?? undefined;
  const modelOs = [device.deviceModel, device.operatingSystem].filter(Boolean).join(" · ") || "—";

  return (
    <div
      className={classNames(
        styles["comp"],
        device.liveStatus === "online" ? styles["comp--online"] : styles["comp--offline"]
      )}
    >
      <button
        type="button"
        className={styles["comp__main"]}
        onClick={onRowClick}
        aria-label={`Open details for ${device.name}`}
      >
        <div className={styles["comp__person"]}>
          <div className={styles["comp__sender"]}>
            <div className={styles["comp__name"]}>{device.name}</div>
            <div className={styles["comp__email"]}>
              {device.organization || device.assignedTo || "—"}
            </div>
          </div>
        </div>

        <div className={styles["comp__category"]}>
          <span className={classNames(styles["comp__badge"], styles["comp__badge--neutral"])}>
            {formatDeviceTypeLabel(device.deviceType)}
          </span>
        </div>

        <div className={styles["comp__model"]}>{modelOs}</div>

        <div className={styles["comp__content"]}>{device.notes}</div>

        <time
          className={styles["comp__timestamp"]}
          dateTime={lastMs ? new Date(lastMs).toISOString() : undefined}
        >
          {formatDeviceTimestamp(lastMs)}
        </time>
      </button>

      <div className={styles["comp__actions"]}>
        <Dropdown>
          <DropdownTrigger className={styles["comp__dropdownTrigger"]}>
            <button
              type="button"
              className={styles["comp__menuTrigger"]}
              aria-label="Device actions"
              aria-haspopup="menu"
              disabled={actionsDisabled}
            >
              <DotsVerticalIcon style={{ width: 20, height: 20 }} aria-hidden />
            </button>
          </DropdownTrigger>
          <DropdownContent width={180} lockBodyScroll={false}>
            <DropdownItem
              id={`${device.id}-edit`}
              selectable={false}
              className={styles["comp__menuItem"]}
              disabled={actionsDisabled}
              onClick={() => onEdit()}
            >
              Edit
            </DropdownItem>
            <DropdownItem
              id={`${device.id}-delete`}
              selectable={false}
              className={classNames(styles["comp__menuItem"], styles["comp__menuItem--danger"])}
              disabled={actionsDisabled}
              onClick={() => onRequestDelete()}
            >
              Delete
            </DropdownItem>
          </DropdownContent>
        </Dropdown>
      </div>
    </div>
  );
};
